import {
    colors,
    OrganizationWithMembers,
    RPUser,
} from '@hazadapt-git/public-core-base'
import { useCallback, useMemo } from 'react'
import {
    inviteUser,
    isUserActive,
    isUserInvited,
    removeUser,
    resendInvitation,
    toast,
    transferOrgOwnership,
    updateOrganization,
    updateOrganizationLogo,
    updateUser,
} from '../utils'
import {
    errorColor,
    infoToastColor,
    primaryIconSize,
    successColor,
} from '../styles/universal'
import {
    IoAlertCircle,
    IoCheckmark,
    IoCheckmarkCircle,
    IoCloudUploadOutline,
    IoTrash,
} from 'react-icons/io5'
import { AxiosError } from 'axios'
import { ErrorResponse, NewRPUser } from '../entities'
import { useAppDispatch, useAppSelector } from '../store'
import { selectFullOrganization, setFullOrganization } from '../slices/base'
import { shallowEqual } from 'react-redux'

export const useOrganization = () => {
    const dispatch = useAppDispatch()
    const fullOrganization = useAppSelector(
        selectFullOrganization,
        shallowEqual
    )

    const handleSaveLogo = useCallback(
        async (file: File) => {
            if (!fullOrganization) return
            try {
                const updatedOrg = await updateOrganizationLogo(file)
                dispatch(setFullOrganization(updatedOrg))
            } catch (err) {
                console.error(err)
            }
        },
        [dispatch, fullOrganization]
    )

    const handleSubmitNewUser = useCallback(
        async (newUser: NewRPUser) => {
            if (!fullOrganization) return
            try {
                const members = await inviteUser([newUser])
                const org = { ...fullOrganization }
                dispatch(
                    setFullOrganization({
                        ...org,
                        members,
                    })
                )
                toast(
                    `${newUser.first_name} ${newUser.last_name} has been invited to your organization.`,
                    <IoCheckmarkCircle
                        color={successColor}
                        size={primaryIconSize}
                    />
                )
            } catch (err) {
                const error = err as AxiosError<ErrorResponse>
                console.error(error)
                toast(
                    error.response?.data.error ||
                        `There was a problem inviting ${newUser.first_name} ${newUser.last_name} to your organization. Please try again.`,
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                    'error'
                )
            }
        },
        [dispatch, fullOrganization]
    )

    const handleRemoveUser = useCallback(
        async (user: RPUser) => {
            if (!fullOrganization) return
            try {
                toast(
                    `Removing ${user.first_name} ${user.last_name}...`,
                    <IoTrash color={infoToastColor} size={primaryIconSize} />
                )
                const orgWithUpdatedMemberList = await removeUser([
                    user.rp_user_id,
                ])
                dispatch(setFullOrganization(orgWithUpdatedMemberList))
                toast(
                    `${user.first_name} ${user.last_name} has been removed from your organization.`,
                    <IoCheckmarkCircle
                        color={successColor}
                        size={primaryIconSize}
                    />
                )
            } catch (e) {
                toast(
                    `There was a problem removing ${user.first_name} ${user.last_name} from your organization. Please try again.`,
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                    'error'
                )
            }
        },
        [dispatch, fullOrganization]
    )

    const handleTransferOwnership = useCallback(
        async (user: RPUser) => {
            if (!fullOrganization) return
            try {
                const newMemberList = await transferOrgOwnership(
                    user.rp_user_id
                )
                if (newMemberList) {
                    const org = { ...fullOrganization }
                    dispatch(
                        setFullOrganization({
                            ...org,
                            members: newMemberList,
                        })
                    )
                    toast(
                        `${user.first_name} ${user.last_name} is now the owner of this space!`,
                        <IoCheckmarkCircle
                            color={successColor}
                            size={primaryIconSize}
                        />
                    )
                }
            } catch (err) {
                const error = err as AxiosError<ErrorResponse>
                console.error(error)
                toast(
                    error.response?.data.error ||
                        'Unable to transfer ownership. Please try again.',
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                    'error'
                )
            }
        },
        [dispatch, fullOrganization]
    )

    const handleResendInvite = useCallback(
        async (user: RPUser) => {
            if (!fullOrganization) return
            if (isUserActive(user)) {
                toast(
                    `${user.first_name} ${user.last_name} has already joined the organization.`
                )
                return
            }
            if (!isUserInvited(user)) return
            try {
                const members = await resendInvitation(user.rp_user_id)
                const org = { ...fullOrganization }
                dispatch(
                    setFullOrganization({
                        ...org,
                        members,
                    })
                )
                toast(
                    `${user.first_name} ${user.last_name} has been re-sent an invitation to your organization.`,
                    <IoCheckmarkCircle
                        color={successColor}
                        size={primaryIconSize}
                    />
                )
            } catch (err) {
                console.error(err)
                toast(
                    `There was a problem re-sending the invitation to ${user.first_name} ${user.last_name}. Please try again.`,
                    <IoAlertCircle color={errorColor} size={primaryIconSize} />,
                    'error'
                )
            }
        },
        [dispatch, fullOrganization]
    )

    const handleSaveOrganizationChanges = useCallback(
        async (fullOrganization: OrganizationWithMembers) => {
            toast(
                'Saving...',
                <IoCloudUploadOutline
                    color={colors.primary.BLUEBERRY}
                    size={primaryIconSize}
                />
            )
            try {
                const updatedOrg = await updateOrganization(fullOrganization)
                dispatch(setFullOrganization(updatedOrg))
                toast(
                    'Changes saved!',
                    <IoCheckmark size={primaryIconSize} color={successColor} />
                )
            } catch (err) {
                const error = err as AxiosError<ErrorResponse>
                console.error(error)
                toast(
                    error.response?.data.error || 'Unable to save changes.',
                    <IoAlertCircle size={primaryIconSize} color={errorColor} />,
                    'error'
                )
            }
        },
        [dispatch]
    )

    const handleSaveUserChanges = useCallback(
        async (user: RPUser) => {
            if (!fullOrganization) return
            const members = [...fullOrganization.members]
            const idx = members.findIndex(
                (m) => m.rp_user_id === user.rp_user_id
            )
            if (idx < 0) return
            members[idx] = {
                ...members[idx],
                ...user,
            }
            try {
                const updatedMembers = await updateUser(user)
                dispatch(
                    setFullOrganization({
                        ...fullOrganization,
                        members: updatedMembers,
                    })
                )
                toast(
                    'Changes saved!',
                    <IoCheckmark size={primaryIconSize} color={successColor} />
                )
            } catch (err) {
                const error = err as AxiosError<ErrorResponse>
                toast(
                    error.response?.data.error || 'Unable to save changes.',
                    <IoAlertCircle size={primaryIconSize} color={errorColor} />,
                    'error'
                )
                throw error // So any UI elements with catch logic can catch this too
            }
        },
        [dispatch, fullOrganization]
    )

    const unresolvedSeatCount = useMemo(() => {
        if (!fullOrganization) return 0
        return Math.max(
            fullOrganization.members.length - fullOrganization.seats,
            0
        )
    }, [fullOrganization])

    return {
        fullOrganization,
        handleSaveLogo,
        handleRemoveUser,
        handleTransferOwnership,
        handleResendInvite,
        handleSaveUserChanges,
        handleSubmitNewUser,
        handleSaveOrganizationChanges,
        unresolvedSeatCount,
    }
}
